/**
 * Crop Editor modal (screen 1d) — the FULL print editor ("Adjustments"). Left
 * canvas with an aspect-locked crop box (2px white border, 9999px dim shadow,
 * rule-of-thirds, corner handles, "{SIZE} AREA" label) + zoom / ⟳ Rotate /
 * Reset; right 320px panel with size dropdown (price + "Custom size…" creator),
 * a ★ suggested-size chip, PAPER/FINISH picker with the ⓘ paper info dialog,
 * Fill/Fit toggle (Fit hidden when the photo already matches the ratio),
 * QUANTITY, a stocked-frames dropdown, BORDER options, a Colour / Black &
 * white toggle, live resolution meter and an "Add to cart" primary action.
 *
 * "ADD A PRINT" MODE (`mode="add"`): the same editor opened from a photo
 * tile's ✎ or the lightbox "Order print" button with a DRAFT print (not in the
 * store). Save calls actions.addPrintFromEditor, so cancelling leaves the
 * order untouched.
 *
 * ROTATION (round 6): ⟳ Rotate ACTUALLY rotates the image 90° clockwise per
 * tap and persists `rotation` on the print. The crop box (and crop_rect)
 * operates on the ROTATED image, DPI math uses the rotated dims (w/h swap at
 * 90/270), and the stage renders the image rotated. The old crop-box-swap
 * rotate is gone.
 *
 * WHITE BORDER preview (round 6): the white border renders as the PRINT SHEET
 * — a white band drawn AROUND the crop box (box = your photo area; band + box
 * = the final print size), with a caption naming the width. No shrunken inner
 * copy of the image; the crop handles stay on the image area. Handling =
 * extra paper drawn around the print. Framed prints lock size (set by the
 * frame) and border (read-only, set by the frame config) and show a framed
 * mock in the panel.
 *
 * INTERACTION MODEL — box within a fixed image (see lib/cropMath): the photo
 * is contain-fitted once; dragging moves the box, corner handles resize it
 * around the opposite corner, the zoom slider shrinks the box about its
 * centre. The box rect in normalized (rotated-)image coordinates IS the
 * CropRect saved via updatePrint. PANORAMA sizes default to the WIDE
 * orientation regardless of the photo's orientation.
 */

import {
  framedProduct,
  parseSizeId,
  borderModesFor,
  resolveBorder,
  type BorderMode,
  type FramedProduct,
  type PricingData,
  type ResolvedSize,
} from '@rpl/pricing';
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import {
  MAX_ZOOM,
  PANO_MAX_ZOOM,
  centerRectAt,
  maxAspectRect,
  moveRect,
  normAspect,
  rectForZoom,
  resizeFromCorner,
  zoomOf,
  type Corner,
} from '../../lib/cropMath';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { customResolvedSize, isCustomSizeId } from '../../lib/customSize';
import { formatMpDims } from '../../lib/format';
import { effectiveDpi } from '../../lib/printCheck';
import { nextRotation, rotatedDims, type Rotation } from '../../lib/rotate';
import { BW_STRENGTH_DEFAULT, BW_STRENGTH_STEP, BW_STYLES, bwCssFilter } from '../../lib/bw';
import {
  borderMeasure,
  borderModeLabel,
  dpiTier,
  dpiTierKind,
  dpiTierLabel,
  effectiveBorderFor,
  fitOptionVisible,
  printRatioLabel,
  ratioLabel,
  sizeShort,
  whiteBorderImageRect,
} from '../../lib/sizePaper';
import type {
  BwStyle, CropRect, FitMode, LocalPhoto, UiPrint } from '../../lib/types';
import { useStore } from '../../store/store';
import { Frame3DView } from '../cart/frame/Frame3DView';
import { ModalShell } from '../sizepaper/ModalShell';
import { Segmented } from '../sizepaper/Segmented';
import type { OrderActions } from '../sizepaper/useOrderActions';
import './crop.css';

export interface CropEditorProps {
  /** The print being edited (crop-only — everything else is fixed from it). */
  print: UiPrint;
  photo: LocalPhoto;
  pricing: PricingData;
  catalog: Map<string, ResolvedSize>;
  actions: OrderActions;
  /** Fired after a successful save (parents toast + pulse the cart badge);
   *  `framed` = the saved print carries a frame. */
  onSaved?: (r: { framed: boolean }) => void;
  /** STAGED mode (Customise pre-Add adjustments, owner 2026-07-27): called
   *  INSTEAD of saveCropEdit — nothing joins the order until Add. The print
   *  prop is a draft in this mode. */
  onApply?: (edit: {
    fitMode: FitMode;
    cropRect: CropRect | null;
    rotation: Rotation;
    bw: boolean;
    bwStyle?: BwStyle | null;
    bwStrength?: number | null;
    borderMode?: BorderMode;
    borderMm?: number | null;
  }) => void;
  onClose: () => void;
}

interface DragState {
  mode: 'move' | Corner;
  startX: number;
  startY: number;
  startRect: CropRect;
  /** Set once the pointer EVER exceeds the click slop — a wiggle-and-return
   *  drag must not be misread as a click at pointer-up (net displacement lies). */
  moved?: boolean;
}

interface PxBox {
  left: number;
  top: number;
  w: number;
  h: number;
}

const CORNERS: Corner[] = ['nw', 'ne', 'sw', 'se'];
const NO_FRAME = 'none';
/** Pointer down→up movement under this many px counts as a CLICK, not a drag. */
const CLICK_SLOP_PX = 5;

/** Contain-fit an aspect (w/h) within an outer box. */
function containBox(outerW: number, outerH: number, aspect: number): { w: number; h: number } {
  let w = outerW;
  let h = aspect > 0 ? outerW / aspect : 0;
  if (h > outerH) {
    h = outerH;
    w = outerH * aspect;
  }
  return { w, h };
}

export function CropEditor({
  print,
  photo,
  pricing,
  catalog,
  actions,
  onSaved,
  onApply,
  onClose,
}: CropEditorProps) {
  /* Owner 2026-07-27: the dialog is CROP-ONLY. Size, paper, quantity, frame
     and border all live in the Customise pane (they duplicated it here and
     made the panel scroll); this dialog keeps crop, zoom, rotate, fill/fit
     and colour. Everything else is FIXED from the print being edited. */
  const sizeId = print.sizeId;
  const paper = print.paper;
  const qty = print.qty;
  const isMobile = useIsMobile();
  const [fitMode, setFitMode] = useState<FitMode>(print.fitMode);
  const [rotation, setRotation] = useState<Rotation>(print.rotation ?? 0);
  const [bw, setBw] = useState<boolean>(print.bw ?? false);
  /* B&W styles (owner 2026-07-31, round 3): turning B&W on is instant and
     defaults to style 1 — the straight conversion. A compact row expands
     under the seg: mini-selector 1|2|3, plus an unlabelled strength slider
     for 2/3 only (style 1 IS the straight conversion, nothing to vary).
     Strength blends the conversion from straight (0) to the style's full
     recipe (100), default mid — the round-2 "hardened" looks are the ceiling,
     not the default. Steps of 10 map 1:1 onto the App-root filter defs. */
  const [bwStyle, setBwStyle] = useState<BwStyle | null>(print.bwStyle ?? null);
  const [bwStrength, setBwStrength] = useState<number>(
    print.bwStrength ?? BW_STRENGTH_DEFAULT,
  );
  /* Border context. Round 2026-07-27 made this dialog crop-only; owner
     2026-07-31 brought BORDER back (selection + custom width) as one compact
     row, keeping the no-scroll property. `borderTouched` gates what save()
     sends: an untouched dialog must not quietly "decide" a fine-art border
     that the Continue gate is still meant to force. */
  const initialBorder: BorderMode | null = print.frameId
    ? 'none'
    : parseSizeId(print.sizeId).family === 'fine_art' && !print.borderDecided
      ? null
      : print.borderMode ?? 'none';
  const [border, setBorder] = useState<BorderMode | null>(initialBorder);
  const [borderTouched, setBorderTouched] = useState(false);
  const [borderMm, setBorderMm] = useState<number | null>(print.borderMm ?? null);
  const [customOpen, setCustomOpen] = useState<boolean>(print.borderMm != null);
  const pickBorder = (m: BorderMode) => {
    setBorder(m);
    setBorderTouched(true);
    if (m !== 'white') {
      setBorderMm(null);
      setCustomOpen(false);
    }
  };

  /** Catalog lookup that also resolves session custom sizes deterministically. */
  const entryFor = (key: string): ResolvedSize | undefined =>
    catalog.get(key) ?? (isCustomSizeId(key) ? customResolvedSize(key) : undefined);

  const entry = entryFor(sizeId);
  const inches = entry?.inches ?? [4, 6];
  const shortIn = Math.min(inches[0], inches[1]);
  const longIn = Math.max(inches[0], inches[1]);

  /* ---------- rotation-aware image dims (crop + DPI space) ---------- */
  const [rotW, rotH] = rotatedDims(photo.width, photo.height, rotation);

  /** Crop-box orientation for a size: panos are ALWAYS wide, else follow the (rotated) image. */
  const boxLandscapeFor = (e: ResolvedSize | undefined, rw: number, rh: number): boolean =>
    e?.pano ? true : rw >= rh;

  const aspectFor = (e: ResolvedSize | undefined, rot: Rotation): number => {
    const [rw, rh] = rotatedDims(photo.width, photo.height, rot);
    const iShort = Math.min(e?.inches[0] ?? 4, e?.inches[1] ?? 6);
    const iLong = Math.max(e?.inches[0] ?? 4, e?.inches[1] ?? 6);
    const pxAspect = boxLandscapeFor(e, rw, rh) ? iLong / iShort : iShort / iLong;
    return normAspect(pxAspect, rw, rh);
  };

  const [rect, setRect] = useState<CropRect>(() => {
    const a0 = aspectFor(entryFor(print.sizeId), print.rotation ?? 0);
    if (print.cropRect) {
      const rectA = print.cropRect.w / print.cropRect.h;
      if (Math.abs(rectA - a0) / a0 < 0.01) return print.cropRect;
    }
    return maxAspectRect(a0);
  });

  const a = aspectFor(entry, rotation);
  const maxZoom = entry?.pano ? PANO_MAX_ZOOM : MAX_ZOOM;
  const minW = maxAspectRect(a).w / maxZoom;
  const zoom = zoomOf(rect, a);

  /* ---------- fit availability: hidden when the photo already matches ---------- */
  const fitAvailable = fitOptionVisible(photo.width, photo.height, [inches[0], inches[1]], actions.thresholds.ratioTolerance);
  useEffect(() => {
    if (!fitAvailable && fitMode === 'fit') setFitMode('crop');
  }, [fitAvailable, fitMode]);

  /* ---------- framed print: live 3D preview while cropping ---------- */
  const framed = !!print.frameId;
  const framedMock = useMemo<FramedProduct | null>(() => {
    if (!print.frameId) return null;
    try {
      return framedProduct(pricing, print.frameId);
    } catch {
      return null;
    }
  }, [pricing, print.frameId]);

  /* ---------- stage measurement (contain-fit the rotated photo) ---------- */
  const stageRef = useRef<HTMLDivElement>(null);
  const [stage, setStage] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => setStage({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* ---------- border → canvas overlay geometry ---------- */
  const displayBorder = framed ? 'none' : effectiveBorderFor(sizeId, border);
  const boxLandscapeNow = boxLandscapeFor(entry, rotW, rotH);
  const printW = boxLandscapeNow ? longIn : shortIn; // print inches in display orientation
  const printH = boxLandscapeNow ? shortIn : longIn;
  const borderRes = useMemo(() => {
    if (displayBorder === 'none') return null;
    try {
      return resolveBorder(pricing, sizeId, displayBorder);
    } catch {
      return null;
    }
  }, [pricing, sizeId, displayBorder]);
  /* Cap mirrors worker validation: a border may eat at most a quarter of the
     short edge per side, so a print can never be all border. */
  const maxBorderMm = Math.max(2, Math.floor((Math.min(printW, printH) * 25.4) / 4));
  const borderResEff = borderRes && displayBorder === 'white' && borderMm != null
    ? { ...borderRes, mm: borderMm }
    : borderRes;
  const borderIn = borderResEff ? borderResEff.mm / 25.4 : 0;

  const rotAspect = rotH > 0 ? rotW / rotH : 1;
  /* Crop-mode border bands (the white print sheet / fine-art handling paper)
     draw OUTSIDE the crop box. The photo used to fill the whole stage, so a
     max crop pushed the band past the stage edge and overflow:hidden ate it
     (owner report 2026-07-31: 4x6 is height-limited, so its band clipped top
     and bottom; every aspect clips on its own constrained axis). Reserve the
     band's fraction of the footprint in the display budget so photo + band
     always fit the stage together. Fit mode builds from the sheet inward and
     never needed this. */
  const bandFrac = (() => {
    if (fitMode !== 'crop' || !borderResEff) return { w: 1, h: 1 };
    if (displayBorder === 'white') {
      const r = whiteBorderImageRect(printW, printH, borderResEff.mm);
      return r.w > 0 && r.h > 0 ? { w: r.w, h: r.h } : { w: 1, h: 1 };
    }
    if (displayBorder === 'handling') {
      return {
        w: printW / (printW + 2 * borderIn),
        h: printH / (printH + 2 * borderIn),
      };
    }
    return { w: 1, h: 1 };
  })();
  const { w: dispW, h: dispH } = containBox(
    stage.w * bandFrac.w, stage.h * bandFrac.h, rotAspect);
  const imgLeft = (stage.w - dispW) / 2;
  const imgTop = (stage.h - dispH) / 2;

  /**
   * The photo rendered ROTATED so its footprint exactly fills `box` (px, in
   * stage/sheet coordinates). At 90/270 the un-rotated element swaps w/h and
   * is centred in the footprint before rotating. B&W applies as a CSS filter.
   */
  const rotatedImg = (box: PxBox, className = 'crope__img') => {
    if (!photo.previewUrl || box.w <= 0 || box.h <= 0) return null;
    const swap = rotation === 90 || rotation === 270;
    const iw = swap ? box.h : box.w;
    const ih = swap ? box.w : box.h;
    const style: CSSProperties = {
      left: box.left + (box.w - iw) / 2,
      top: box.top + (box.h - ih) / 2,
      width: iw,
      height: ih,
      ...(rotation !== 0 ? { transform: `rotate(${rotation}deg)` } : {}),
      ...(bw ? { filter: bwCssFilter(bw, bwStyle, bwStrength) } : {}),
    };
    return <img className={className} style={style} src={photo.previewUrl} alt={photo.filename} draggable={false} />;
  };

  /* ---------- pointer interactions ---------- */
  const dragRef = useRef<DragState | null>(null);
  // Clarity heatmaps show heavy dead clicks on the crop box: users TAP it
  // expecting a response, but it only reacts to a drag. Show an on-box "drag"
  // affordance until they actually move it, so the interaction is discoverable.
  const [hasDragged, setHasDragged] = useState(false);

  const beginDrag = (mode: DragState['mode']) => (e: ReactPointerEvent<HTMLElement>) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { mode, startX: e.clientX, startY: e.clientY, startRect: rect };
  };

  const onDragMove = (e: ReactPointerEvent<HTMLElement>) => {
    const d = dragRef.current;
    if (!d || dispW === 0 || dispH === 0) return;
    if (!hasDragged) setHasDragged(true);
    if (
      !d.moved &&
      (Math.abs(e.clientX - d.startX) >= CLICK_SLOP_PX ||
        Math.abs(e.clientY - d.startY) >= CLICK_SLOP_PX)
    ) {
      d.moved = true; // it's a drag now, whatever the release point says
    }
    const dx = (e.clientX - d.startX) / dispW;
    const dy = (e.clientY - d.startY) / dispH;
    setRect(
      d.mode === 'move'
        ? moveRect(d.startRect, dx, dy)
        : resizeFromCorner(d.startRect, d.mode, dx, dy, a, minW),
    );
  };

  const endDrag = () => {
    dragRef.current = null;
  };

  /* ---------- click-to-position (Clarity: 78% of crop-box clicks were dead) ----------
     A plain click/tap (< ~5px of movement) on the stage or the box re-centres
     the crop rect toward the clicked point, clamped to the image — like
     clicking in most photo apps. Real drags are untouched: any movement past
     the slop threshold falls through to the existing drag path. */
  const clickRepositions = (e: ReactPointerEvent<HTMLElement>, startX: number, startY: number) => {
    if (
      Math.abs(e.clientX - startX) >= CLICK_SLOP_PX ||
      Math.abs(e.clientY - startY) >= CLICK_SLOP_PX
    )
      return;
    const stage = stageRef.current;
    if (!stage || dispW === 0 || dispH === 0) return;
    const sr = stage.getBoundingClientRect();
    const nx = (e.clientX - sr.left - imgLeft) / dispW;
    const ny = (e.clientY - sr.top - imgTop) / dispH;
    setRect((r) => centerRectAt(r, nx, ny));
    if (!hasDragged) setHasDragged(true); // interaction discovered — retire the hint
  };

  /** Box pointer-up: a tap repositions; a drag already moved the box. */
  const endBoxDrag = (e: ReactPointerEvent<HTMLElement>) => {
    const d = dragRef.current;
    dragRef.current = null;
    if (d && d.mode === 'move' && !d.moved) clickRepositions(e, d.startX, d.startY);
  };

  /** Stage clicks (outside the box) re-centre too. The box stops propagation
   *  on pointer-down, so these only see presses that started off the box. */
  const stageDownRef = useRef<{ x: number; y: number } | null>(null);
  const onStagePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    if (fitMode !== 'crop') return;
    stageDownRef.current = { x: e.clientX, y: e.clientY };
  };
  const onStagePointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const s = stageDownRef.current;
    stageDownRef.current = null;
    if (s && fitMode === 'crop') clickRepositions(e, s.x, s.y);
  };

  /* ---------- derived: resolution + copy (friendly tiered labels) ---------- */
  /* A white border is INSET: the sheet stays the size the customer picked and
     the IMAGE shrinks inside it, so the same pixels cover a smaller area and the
     true DPI is HIGHER. The number used to divide by the full sheet, so it never
     moved when the border slider did (owner 2026-08-31) and understated
     resolution by ~5-7% at the default widths. A handling border is OUTSET — the
     sheet grows, the image does not — so it must NOT be subtracted.
     Display only: this feeds the readout and its advisory meter. The low-res
     GATE (lib/printCheck + the Worker's authoritative copy) still measures
     against the full sheet, so nothing that is blocked today starts passing. */
  const insetIn = borderResEff?.geometry === 'inset' ? borderIn : 0;
  const imageIn: [number, number] = [
    Math.max(0.1, inches[0] - 2 * insetIn),
    Math.max(0.1, inches[1] - 2 * insetIn),
  ];
  const dpi =
    fitMode === 'fit'
      ? effectiveDpi(rotW, rotH, imageIn)
      : effectiveDpi(rect.w * rotW, rect.h * rotH, imageIn);
  const sizeFamily = parseSizeId(sizeId).family;
  const tier = dpiTier(dpi, sizeFamily, actions.thresholds);
  const meterKind = dpiTierKind(tier);
  const meterWord = dpiTierLabel(tier);
  const size = sizeShort(parseSizeId(sizeId).size);


  const imgRatio = ratioLabel(photo.width, photo.height);
  const printRatio = printRatioLabel([inches[0], inches[1]]);
  const ratioMismatch = imgRatio !== printRatio;
  // Round 7: a print that already carries a saved crop re-opens as "Custom
  // crop", not the auto-crop mismatch note — the decision was made.
  const customCropSaved = print.fitMode === 'crop' && print.cropRect !== null;
  const explainer = entry?.pano
    ? `${size}" prints a wide strip. Drag the box to choose what's kept.`
    : fitMode === 'fit'
      ? `Fit keeps the whole photo and adds white space where it doesn't fill the ${size}" sheet.`
      : ratioMismatch
        ? customCropSaved
          ? `Custom crop set. The shaded edges are trimmed — drag the box to change what's kept.`
          : `Your ${imgRatio} photo doesn't match ${size}" (${printRatio}) exactly. The shaded edges will be trimmed. Drag the box to choose what's kept.`
        : customCropSaved
          ? `Custom crop set. Drag or zoom the box to adjust it.`
          : `This photo matches ${size}" exactly. Zoom in for a tighter crop if you like.`;

  const rotate = () => {
    const next = nextRotation(rotation);
    setRotation(next);
    // The crop space changed under the box — restart from the max centered crop.
    setRect(maxAspectRect(aspectFor(entry, next)));
  };

  const reset = () => {
    setRotation(0);
    setRect(maxAspectRect(aspectFor(entry, 0)));
  };

  const save = () => {
    const effFit: FitMode = fitAvailable ? fitMode : 'crop';
    const patch = {
      fitMode: effFit,
      cropRect: effFit === 'crop' ? rect : null,
      rotation,
      bw,
      bwStyle: bw ? bwStyle : null,
      bwStrength:
        bw && (bwStyle === 'v2' || bwStyle === 'v3') ? bwStrength : null,
      // Only when the customer actually used the border row: an untouched
      // dialog must not decide an undecided fine-art border.
      ...(borderTouched && border != null
        ? { borderMode: border, borderMm: border === 'white' ? borderMm : null }
        : {}),
    };
    if (onApply) {
      // Staged mode: hand the adjustments back to the Customise pane.
      onApply(patch);
      onClose();
      return;
    }
    actions.saveCropEdit(print.id, {
      sizeId,
      ...patch,
      qty,
      paper,
      // Size/paper/frame/border are locked in this dialog — the print's own
      // frame passes straight through (never unframes).
      frameId: print.frameId ?? null,
    });
    // Owner 2026-08-01 bug: the Customise pane keys its border SELECTOR (and
    // its previewPrint lookup) off spBorder — after saving a border here the
    // pane still selected the old mode, previewed no band, and a further Add
    // would open a SEPARATE borderless line. Explicit sync on save only (a
    // blind effect would fight deliberately picking another border variant).
    if (!framed && borderTouched && border != null) {
      useStore.getState().setSpBorder(border);
    }
    onSaved?.({ framed });
    onClose();
  };

  /* ---------- fit-mode sheet preview geometry ---------- */
  const sheetPxAspect = boxLandscapeNow ? longIn / shortIn : shortIn / longIn;
  const { w: sheetW, h: sheetH } = containBox(stage.w, stage.h, sheetPxAspect);

  const boxLeft = imgLeft + rect.x * dispW;
  const boxTop = imgTop + rect.y * dispH;
  const boxW = rect.w * dispW;
  const boxH = rect.h * dispH;
  const boxStyle: CSSProperties = { left: boxLeft, top: boxTop, width: boxW, height: boxH };

  /**
   * WHITE border, crop mode (round 6): the final PRINT SHEET drawn around the
   * crop box — a solid white band whose outer edge is the print size. The box
   * (your photo area) stays untouched, handles included; the band carries the
   * stage dim so everything outside the sheet darkens.
   */
  const whiteSheetBand = () => {
    if (displayBorder !== 'white' || !borderResEff || boxW <= 0 || boxH <= 0) return null;
    const r = whiteBorderImageRect(printW, printH, borderResEff.mm);
    if (r.w <= 0 || r.h <= 0) return null;
    const sheetPxW = boxW / r.w;
    const sheetPxH = boxH / r.h;
    const left = Math.max(0, r.x * sheetPxW);
    const top = Math.max(0, r.y * sheetPxH);
    const right = Math.max(0, sheetPxW - boxW - left);
    const bottom = Math.max(0, sheetPxH - boxH - top);
    return (
      <div
        className="crope__sheetband"
        style={{
          left: boxLeft - left,
          top: boxTop - top,
          width: sheetPxW,
          height: sheetPxH,
          borderWidth: `${top}px ${right}px ${bottom}px ${left}px`,
        }}
        aria-hidden
      />
    );
  };

  /** Handling border: extra blank paper drawn AROUND a print box. */
  const handlingBand = (left: number, top: number, pxW: number, pxH: number) => {
    if (displayBorder !== 'handling' || !borderResEff || pxW <= 0 || pxH <= 0) return null;
    const hx = (borderIn / printW) * pxW;
    const hy = (borderIn / printH) * pxH;
    return (
      <div
        className="crope__hband"
        style={{
          left: left - hx,
          top: top - hy,
          width: pxW + 2 * hx,
          height: pxH + 2 * hy,
          borderWidth: `${hy}px ${hx}px`,
        }}
        aria-hidden
      />
    );
  };

  /** Fit-mode: the rotated photo contained within a px box (sheet or content area). */
  const fitImage = (outer: PxBox) => {
    const c = containBox(outer.w, outer.h, rotAspect);
    return rotatedImg({
      left: outer.left + (outer.w - c.w) / 2,
      top: outer.top + (outer.h - c.h) / 2,
      w: c.w,
      h: c.h,
    });
  };

  const wbCaption =
    displayBorder === 'white' && borderRes
      ? `White border: ${borderMeasure(borderResEff ? borderResEff.mm : 0)}. Your photo prints smaller inside the sheet.`
      : null;

  const heading = `Edit print · ${photo.filename}`;

  return (
    <ModalShell onClose={onClose} width={1080} label={heading} className="crope">
      <div className="crope__head">
        <div className="crope__title-wrap">
          <span className="crope__title">{heading}</span>
          <span className="crope__meta">{formatMpDims(photo.width, photo.height)} px</span>
        </div>
        <button type="button" className="crope__close" aria-label="Close" onClick={onClose}>
          ✕
        </button>
      </div>

      <div className="crope__body">
        <div className="crope__canvas">
          <div
            ref={stageRef}
            className="crope__stage"
            onPointerDown={onStagePointerDown}
            onPointerUp={onStagePointerUp}
            onPointerCancel={() => (stageDownRef.current = null)}
          >
            {fitMode === 'crop' ? (
              <>
                {rotatedImg({ left: imgLeft, top: imgTop, w: dispW, h: dispH })}
                {dispW > 0 && (
                  <>
                    {whiteSheetBand()}
                    <div
                      className={`crope__box${displayBorder === 'white' ? ' crope__box--wb' : ''}`}
                      style={boxStyle}
                      onPointerDown={beginDrag('move')}
                      onPointerMove={onDragMove}
                      onPointerUp={endBoxDrag}
                      onPointerCancel={endDrag}
                    >
                      <span className="crope__third crope__third--v" style={{ left: '33.333%' }} />
                      <span className="crope__third crope__third--v" style={{ left: '66.667%' }} />
                      <span className="crope__third crope__third--h" style={{ top: '33.333%' }} />
                      <span className="crope__third crope__third--h" style={{ top: '66.667%' }} />
                      {CORNERS.map((c) => (
                        <span
                          key={c}
                          className={`crope__handle crope__handle--${c}`}
                          onPointerDown={beginDrag(c)}
                          onPointerMove={onDragMove}
                          onPointerUp={endDrag}
                          onPointerCancel={endDrag}
                        />
                      ))}
                      {!hasDragged && (
                        <span className="crope__draghint" aria-hidden="true">
                          ✥ Drag to reposition
                        </span>
                      )}
                    </div>
                    {handlingBand(boxLeft, boxTop, boxW, boxH)}
                  </>
                )}
              </>
            ) : (
              <>
                <div className="crope__sheet" style={{ width: sheetW, height: sheetH }}>
                  {displayBorder === 'white' && borderRes
                    ? // Fit + white border: the whole photo contains within
                      // (print − 2·border); the sheet's white shows around it.
                      fitImage({
                        left: (borderIn / printW) * sheetW,
                        top: (borderIn / printH) * sheetH,
                        w: sheetW * (1 - (2 * borderIn) / printW),
                        h: sheetH * (1 - (2 * borderIn) / printH),
                      })
                    : fitImage({ left: 0, top: 0, w: sheetW, h: sheetH })}
                </div>
                {handlingBand((stage.w - sheetW) / 2, (stage.h - sheetH) / 2, sheetW, sheetH)}
              </>
            )}
          </div>

          {wbCaption && <div className="crope__wbcaption">{wbCaption}</div>}

          <div className="crope__tools">
            <span className="crope__tool-label">Zoom</span>
            <input
              className="crope__zoom"
              type="range"
              min={1}
              max={maxZoom}
              step={0.01}
              value={Math.min(zoom, maxZoom)}
              disabled={fitMode === 'fit'}
              aria-label="Zoom"
              onChange={(e) => setRect(rectForZoom(rect, a, Number(e.target.value), maxZoom))}
            />
            <button type="button" className="crope__toolbtn" onClick={rotate}>
              ⟳ Rotate
            </button>
            <button
              type="button"
              className="crope__toolbtn"
              onClick={reset}
              disabled={fitMode === 'fit' && rotation === 0}
            >
              Reset
            </button>
          </div>

          {/* Border row (owner 2026-07-31): selection + custom width, one
              compact strip so the dialog keeps its no-scroll property. Framed
              prints keep their frame-fixed border and never show this. */}
          {!framed && (
            <div className="crope__borderrow">
              <span className="crope__tool-label">Border</span>
              {borderModesFor(sizeId, false).map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`crope__toolbtn${(border ?? 'none') === m ? ' crope__toolbtn--on' : ''}`}
                  onClick={() => pickBorder(m)}
                >
                  {borderModeLabel(m)}
                </button>
              ))}
              {border === 'white' && (
                <button
                  type="button"
                  className={`crope__toolbtn${customOpen ? ' crope__toolbtn--on' : ''}`}
                  onClick={() => {
                    if (customOpen) {
                      setBorderMm(null);
                      setBorderTouched(true);
                    }
                    setCustomOpen((v) => !v);
                  }}
                >
                  Custom width
                </button>
              )}
            </div>
          )}
          {!framed && border === 'white' && customOpen && (
            <div className="crope__borderrow crope__borderrow--custom">
              <input
                type="range"
                className="crope__zoom"
                min={2}
                max={maxBorderMm}
                step={0.5}
                value={borderMm ?? (borderRes ? borderRes.mm : 4)}
                aria-label="Border width"
                onChange={(e) => {
                  setBorderMm(Number(e.target.value));
                  setBorderTouched(true);
                }}
              />
              <span className="crope__bordercm">
                {((borderMm ?? (borderRes ? borderRes.mm : 4)) / 10).toFixed(1)} cm
              </span>
            </div>
          )}
        </div>

        <div className="crope__panel">
          {/* Owner 2026-07-27: crop-only dialog. Size/paper/qty/frame/border
              live in the Customise pane — the framed 3D preview stays so
              crop/rotate/B&W render inside the frame while adjusting. */}
          {/* Owner 2026-07-27: DESKTOP only. On phones the 3D frame forces a
              long scroll inside the dialog, and the framed preview is right
              there in the pane the moment you save. Skipping the render also
              keeps its rAF loop from ever starting on mobile. */}
          {framed && framedMock && !isMobile && (
            <div className="crope__framewrap">
              <Frame3DView
                photo={photo}
                option={framedMock}
                cropRect={fitMode === 'crop' ? rect : null}
                rotation={rotation}
                bw={bw}
                bwStyle={bwStyle}
                bwStrength={bwStrength}
                fit={fitMode === 'fit'}
              />
            </div>
          )}

          {fitAvailable && (
            <div>
              <div className="eyebrow crope__eyebrow">Fit mode</div>
              <Segmented
                variant="light"
                className="crope__fitmode"
                ariaLabel="Fill or fit"
                segments={[
                  { key: 'crop', label: 'Fill (crop)' },
                  { key: 'fit', label: 'Fit (adds white space)' },
                ]}
                value={fitMode}
                onChange={(key) => setFitMode(key as FitMode)}
              />
            </div>
          )}

          <div>
            <div className="eyebrow crope__eyebrow">Colour</div>
            <Segmented
              variant="light"
              className="crope__colour"
              ariaLabel="Colour or black and white"
              segments={[
                { key: 'colour', label: 'Colour' },
                { key: 'bw', label: 'Black & white' },
              ]}
              value={bw ? 'bw' : 'colour'}
              onChange={(key) => setBw(key === 'bw')}
            />
            {bw && (
              <div className="crope__bwrow">
                <div className="seg seg--light crope__bwseg" role="group" aria-label="Black and white style">
                  {BW_STYLES.map((st, i) => (
                    <button
                      key={st}
                      type="button"
                      className={`seg__opt${(bwStyle ?? 'v1') === st ? ' seg__opt--on' : ''}`}
                      aria-pressed={(bwStyle ?? 'v1') === st}
                      onClick={() => setBwStyle(st)}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                {(bwStyle === 'v2' || bwStyle === 'v3') && (
                  <input
                    type="range"
                    className="crope__zoom crope__bwslider"
                    min={0}
                    max={100}
                    step={BW_STRENGTH_STEP}
                    value={bwStrength}
                    aria-label="Black and white strength"
                    onChange={(e) => setBwStrength(Number(e.target.value))}
                  />
                )}
              </div>
            )}
          </div>

          <div className="crope__meter">
            <div className="eyebrow crope__meter-eyebrow">Print resolution</div>
            <div className={`crope__meter-word crope__meter-word--${meterKind}`}>{meterWord}</div>
            <div className="crope__meter-bar">
              <span
                className={`crope__meter-fill crope__meter-fill--${meterKind}`}
                style={{ width: `${Math.min(100, (dpi / 500) * 100)}%` }}
              />
            </div>
            <div className="crope__meter-caption">
              {dpi} DPI at {size}&quot;
              {rotation !== 0 ? ` · rotated ${rotation}°` : ''}
            </div>
          </div>

          <div className="crope__explainer">{explainer}</div>

          <div className="crope__actions">
            {/* Honest label (owner 2026-07-26): this dialog saves in place —
                "Add to cart" here read as a silent no-op. */}
            <button type="button" className="btn btn--primary crope__save" onClick={save}>
              Save changes
            </button>
            <button type="button" className="crope__cancel" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>

    </ModalShell>
  );
}
